/**
 * Which invoice world the TENANT DETAIL is showing (TINV-2).
 *
 * THE RULES ARE NOT RESTATED HERE. Every decision below is made by
 * `pages/admin/invoices/useInvoiceSource.js` — `resolveInvoiceSource` decides
 * which world is current, `resolveSourceLabels` decides what each may honestly
 * be called. This module owns exactly one thing the dashboard's own hook cannot
 * lend us: WHERE the reader's choice is kept.
 *
 * WHY THE DASHBOARD'S HOOK IS NOT IMPORTED WHOLE. `useInvoiceSource` stores the
 * choice in `?source=` through `useSearchParams`, which is right for a page whose
 * entire subject is one invoice world and whose links are shared as such. The
 * tenant detail is not that page: its URL already addresses a person, its tab
 * state is React state, and writing an invoice-world parameter into a tenant link
 * would make "send me this tenant" carry an unrelated claim. So the STORAGE is
 * local and the RULES are imported — the split the slice brief asks for, and the
 * reason there is no second provider table, no second label map and no second
 * list of allowed sources anywhere under `tenant/invoices`.
 *
 * THERE IS NO "BOTH". `INVOICE_SOURCES` has two members and the resolver rejects
 * anything else, `"all"` included. Bexio invoices and Vantio invoices are never
 * summed, concatenated or interleaved on this surface — one view, one source.
 */
import { useCallback, useMemo, useState } from "react";

import {
  resolveInvoiceSource,
  resolveSourceLabels,
} from "../../invoices/useInvoiceSource";

/**
 * The organization's CURRENT invoice world, or `null` while that is unknown.
 *
 * Derived by asking the shared resolver what it would choose when the reader has
 * requested nothing — which is, by that function's own last branch, the confirmed
 * provider and nothing else. Written this way rather than as a local
 * `provider === "bexio" || provider === "native" ? provider : null` on purpose:
 * that expression is `confirmedProviderOf`, a rule this file must follow and must
 * not own a copy of. Asking the resolver keeps a single definition of "confirmed"
 * without exporting a private helper out of the dashboard.
 *
 * `null` means NOT YET KNOWN — while the capability read is in flight, and after
 * it failed. It never means "native". Falling back to Vantio on a failed read
 * would tell an organization that bills in Bexio that its books are the archive.
 */
export function currentTenantInvoiceSource({ loading, error, provider }) {
  return resolveInvoiceSource({
    requestedSource: null,
    loading,
    error,
    provider,
  }).source;
}

/**
 * What to CALL each world, for a given shown source and confirmed provider.
 *
 * A pass-through to the shared label rule, kept as a named export so every
 * tenant-detail consumer reaches the words the same way and none of them is
 * tempted to write "Bexio aktuell" as a literal.
 */
export function tenantInvoiceSourceLabels({ source, provider }) {
  return resolveSourceLabels({ source, provider });
}

/**
 * The tenant detail's invoice-world state.
 *
 * `source` is `"bexio"`, `"native"` or `null` — `null` meaning "not yet known",
 * never "empty". A caller must render neither world as current while it is
 * `null`, and must not fetch either.
 *
 * `provider` is the CONFIRMED provider or `null`, so a component can tell "this
 * organization has two worlds" from "we have not been told yet" without
 * inspecting the capability read itself.
 *
 * `setSource` is handed straight to the shared switcher. A value the contract
 * rejects is dropped by the resolver rather than stored, so no unknown word can
 * enter this state.
 */
export default function useTenantInvoiceSource({ loading, error, provider }) {
  // What the reader asked for, or `null` for "whatever this organization is".
  // Starts null so the FIRST render already shows the provider's own world
  // rather than a guess that later flips.
  const [requestedSource, setRequestedSource] = useState(null);

  const confirmedProvider = useMemo(
    () => currentTenantInvoiceSource({ loading, error, provider }),
    [loading, error, provider]
  );

  const source = useMemo(
    () =>
      resolveInvoiceSource({ requestedSource, loading, error, provider })
        .source,
    [requestedSource, loading, error, provider]
  );

  const labels = useMemo(
    () => tenantInvoiceSourceLabels({ source, provider: confirmedProvider }),
    [source, confirmedProvider]
  );

  const setSource = useCallback((next) => {
    setRequestedSource(next);
  }, []);

  return {
    source,
    provider: confirmedProvider,
    labels,
    setSource,
    isResolving: loading,
    error,
  };
}
